import React from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { v4 } from "uuid";
import { CartContext } from "./CartContext";
import { GET_CART } from "../../queries/cartQuery";
import {
  ADD_ITEM_TO_CART,
  UPDATE_ITEM_QUANTITIES,
  EMPTY_CART,
} from "../../queries/mutations/cartMutations";

export const CartContextProvider = (props) => {
  const { data: cartData, loading, error, refetch: refetchCart } = useQuery(GET_CART, {
    notifyOnNetworkStatusChange: true,
  });

  const [addToCart, { error: addToCartError }] = useMutation(ADD_ITEM_TO_CART, {
    refetchQueries: [{ query: GET_CART }],
  });

  const [updateItemQuantities] = useMutation(UPDATE_ITEM_QUANTITIES, {
    refetchQueries: [{ query: GET_CART }],
  });

  const [emptyCartMutation] = useMutation(EMPTY_CART, {
    refetchQueries: [{ query: GET_CART }],
  });

  const cart = cartData?.cart ?? null;

  const getCartKeyByProductId = (productId) => {
    const cartItem = cart?.contents?.nodes?.find(
      (item) => item.product?.node?.productId === productId
    );
    return cartItem?.key;
  };

  const getProductQuantity = (productId) => {
    const item = cart?.contents?.nodes?.find(
      (item) => item?.product?.node?.productId === productId
    );
    return item?.quantity || 0;
  };

  const addProductToCart = async (productId, quantity = 1) => {
    await addToCart({
      variables: {
        clientMutationId: v4(),
        productId: parseInt(productId, 10),
        quantity: parseInt(quantity, 10),
      },
    });
  };

  const updateProductFromCart = async (cartKey, quantity) => {
    await updateItemQuantities({
      variables: {
        input: {
          clientMutationId: v4(),
          items: [{ key: cartKey, quantity }],
        },
      },
    });
  };

  const removeProductFromCart = async (cartKey) => {
    await updateProductFromCart(cartKey, 0);
  };

  const emptyCart = async () => {
    await emptyCartMutation();
  };

  const updateProductQuantity = async (productId, quantity) => {
    const cartKey = getCartKeyByProductId(productId);
    if (!cartKey) {
      return await addProductToCart(productId, quantity);
    }
    return await updateProductFromCart(cartKey, quantity);
  };

  const handleLoadMore = () => {};

  const context = {
    cart,
    cartData,
    items: cart?.contents?.nodes || [],
    total: cart?.total || "",
    subtotal: cart?.subtotal || "",
    isEmpty: cart?.isEmpty ?? true,
    loading,
    error,
    getCartKeyByProductId,
    getProductQuantity,
    updateProductFromCart,
    addProductToCart,
    addProduct: addProductToCart,
    addToCartError,
    updateProductQuantity,
    removeProductFromCart,
    emptyCart,
    refetchCart,
    refetch: refetchCart,
    handleLoadMore,
  };

  return (
    <CartContext.Provider value={context}>
      {props.children}
    </CartContext.Provider>
  );
};
